import { type ComponentProps } from 'react';
import { Root, Image, Fallback } from '@radix-ui/react-avatar';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/util/utils';

type AvatarProps = ComponentProps<typeof Root> & VariantProps<typeof avatarVariants>;

/**
 * ui-avatar: a circle on the sidebar's accent by default, inverted to the muted ground when
 * it sits on a light surface, with the initials in the 12px label style at every size
 * (primitives.js). Sizes from the sidebar (40) and header (32, 24) baselines. `initials="comment"`
 * is the comment feeds' own avatar (.cmt-avatar): 11px Geist on the muted ground.
 */
function Avatar({ className, size, tone, initials, ref, ...props }: AvatarProps) {
    return (
        <Root
            data-slot="avatar"
            className={cn(avatarVariants({ size, tone, initials }), className)}
            ref={ref}
            {...props}
        />
    );
}

const avatarVariants = cva('relative flex shrink-0 overflow-hidden rounded-full', {
    variants: {
        size: {
            sm: 'size-6',
            md: 'size-8',
            lg: 'size-10',
        },
        tone: {
            dark: 'bg-sidebar-accent text-sidebar-accent-foreground',
            light: 'bg-muted text-secondary-foreground',
        },
        initials: {
            label: 'text-label-md',
            comment: 'text-muted-foreground font-sans text-[11px] font-normal tracking-[0.04em]',
        },
    },
    defaultVariants: { size: 'md', tone: 'dark', initials: 'label' },
});

function AvatarImage({ className, ref, ...props }: ComponentProps<typeof Image>) {
    return (
        <Image
            data-slot="avatar-image"
            className={cn('aspect-square size-full object-cover', className)}
            ref={ref}
            {...props}
        />
    );
}

function AvatarFallback({ className, ref, ...props }: ComponentProps<typeof Fallback>) {
    return (
        <Fallback
            data-slot="avatar-fallback"
            className={cn('flex size-full items-center justify-center', className)}
            ref={ref}
            {...props}
        />
    );
}

export { Avatar, AvatarImage, AvatarFallback };
